import type { AiToolkitAssistantProject } from "@mem-client/ai-toolkit/contract";
import type { AssistantProjectObservable } from "@/store/assistant/AssistantProjectObservable";
import {
  AssistantChangeRequestChangeKind,
  AssistantProjectStatus,
  type AssistantProjectChangeRequestModelData,
  type AssistantProjectModelData,
} from "@/store/assistant/types";
import type { SyncUpdateValue } from "@/store/sync/types";

type AssistantProjectSource = Pick<
  AssistantProjectObservable,
  | "id"
  | "assistantId"
  | "title"
  | "description"
  | "status"
  | "lastActivityAt"
  | "createdAt"
  | "updatedAt"
>;

export const buildAssistantProject = ({
  project,
}: {
  project: AssistantProjectSource;
}): AiToolkitAssistantProject => ({
  id: project.id,
  assistantId: project.assistantId,
  title: project.title,
  description: project.description,
  status: project.status,
  lastActivityAt: project.lastActivityAt,
  createdAt: project.createdAt,
  updatedAt: project.updatedAt,
});

export const buildAssistantProjectFromSyncUpdateValue = ({
  updateValue,
}: {
  updateValue: SyncUpdateValue<AssistantProjectModelData>;
}): AiToolkitAssistantProject => {
  const project = updateValue.model_data;

  return {
    id: updateValue.model_id,
    assistantId: project.assistant_id,
    title: project.title,
    description: project.description,
    status: project.status,
    lastActivityAt: project.last_activity_at,
    createdAt: project.created_at,
    updatedAt: project.updated_at,
  };
};

export const isCreateProjectChangeRequestUpdateValue = ({
  updateValue,
}: {
  updateValue: SyncUpdateValue<AssistantProjectChangeRequestModelData>;
}): boolean => updateValue.model_data.change_kind === AssistantChangeRequestChangeKind.Create;

export const buildAssistantProjectFromCreateChangeRequestUpdateValue = ({
  updateValue,
}: {
  updateValue: SyncUpdateValue<AssistantProjectChangeRequestModelData>;
}): AiToolkitAssistantProject => {
  const changeRequest = updateValue.model_data;

  return {
    id: updateValue.model_id,
    assistantId: changeRequest.assistant_id,
    title: changeRequest.requested_title ?? "",
    description: changeRequest.requested_description,
    status: changeRequest.requested_status ?? AssistantProjectStatus.Active,
    lastActivityAt: null,
    createdAt: changeRequest.created_at,
    updatedAt: changeRequest.updated_at,
    pending: true,
  };
};
